import React from 'react';
import type { RecordingAgreementFormData } from './chat-script';

interface RecordingAgreementPreviewProps {
  formData: RecordingAgreementFormData;
}

const V: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-white font-medium">{children}</span>
);

const Placeholder: React.FC<{ label: string }> = ({ label }) => (
  <span className="text-white/30 font-medium">[{label}]</span>
);

const Field: React.FC<{ value: string; label: string }> = ({ value, label }) =>
  value ? <V>{value}</V> : <Placeholder label={label} />;

export const RecordingAgreementPreview: React.FC<RecordingAgreementPreviewProps> = ({
  formData,
}) => {
  const {
    artistName,
    labelCompany,
    advanceAmount,
    royaltySplit,
    territory,
    contractDuration,
    albumOptions,
  } = formData;

  const labelShare = 100 - royaltySplit;

  // Parse album options
  const firmAlbums = albumOptions.match(/^(\d+)\s*Album/)?.[1] ?? '';
  const optionAlbums = albumOptions.match(/\+\s*(\d+)\s*Option/)?.[1] ?? '0';

  return (
    <div className="max-w-none text-neutral-300 text-sm leading-relaxed space-y-6">
      {/* Header */}
      <div className="text-center pb-4 border-b border-white/10">
        <h1 className="text-2xl font-bold text-white tracking-wide">
          RECORDING AGREEMENT
        </h1>
        <p className="text-neutral-400 text-xs mt-1 uppercase tracking-widest">
          Confidential
        </p>
      </div>

      {/* Preamble */}
      <p>
        This Recording Agreement (this "Agreement") is entered into as of the date last
        signed below, by and between{' '}
        <Field value={labelCompany} label="LABEL" /> (the "Label") and{' '}
        <Field value={artistName} label="ARTIST" /> (the "Artist"), collectively
        referred to as the "Parties."
      </p>

      <p>
        WHEREAS, the Label desires to engage the Artist to produce and deliver sound
        recordings on an exclusive basis, and the Artist desires to render such services,
        subject to the terms and conditions set forth herein.
      </p>

      <p>
        NOW, THEREFORE, in consideration of the mutual covenants contained herein and for
        other good and valuable consideration, the receipt and sufficiency of which are
        hereby acknowledged, the Parties agree as follows:
      </p>

      {/* Section 1: Recording Commitment */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-2">
          1. Recording Commitment
        </h2>
        <p>
          1.1 During the Term, the Artist shall deliver to the Label{' '}
          <Field value={firmAlbums ? `${firmAlbums} (${numberWord(firmAlbums)})` : ''} label="NUMBER" />{' '}
          full-length studio album(s) (each an "Album"), each consisting of no fewer
          than ten (10) master recordings suitable for commercial release.
        </p>
        {optionAlbums !== '0' && (
          <p className="mt-2">
            1.2 The Label shall have the irrevocable option to require the Artist to
            record and deliver up to{' '}
            <V>
              {optionAlbums} ({numberWord(optionAlbums)})
            </V>{' '}
            additional Album(s) (each an "Option Album") upon written notice to the
            Artist within sixty (60) days following delivery and acceptance of the
            preceding Album.
          </p>
        )}
      </div>

      {/* Section 2: Compensation */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-2">2. Compensation</h2>
        <p>
          2.1 <span className="font-medium text-white">Advance.</span> The Label shall
          pay the Artist a recoupable advance of{' '}
          <Field value={advanceAmount ? `$${advanceAmount} USD` : ''} label="ADVANCE" />{' '}
          (the "Advance"), payable upon full execution of this Agreement.
        </p>
        <p className="mt-2">
          2.2 <span className="font-medium text-white">Royalties.</span> Following
          complete recoupment of the Advance and all recoupable costs, the Artist shall
          receive{' '}
          <Field
            value={royaltySplit ? `${royaltySplit}%` : ''}
            label="ARTIST %"
          />{' '}
          of net royalty income derived from exploitation of the master recordings, with
          the remaining{' '}
          <Field
            value={labelShare ? `${labelShare}%` : ''}
            label="LABEL %"
          />{' '}
          retained by the Label.
        </p>
        <p className="mt-2">
          2.3 <span className="font-medium text-white">Accounting.</span> The Label
          shall render royalty statements and payments to the Artist on a semi-annual
          basis, within ninety (90) days following the end of each accounting period.
        </p>
      </div>

      {/* Section 3: Territory & Term */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-2">
          3. Territory & Term
        </h2>
        <p>
          3.1 <span className="font-medium text-white">Territory.</span> The rights
          granted hereunder shall apply to{' '}
          <Field value={territory} label="TERRITORY" />.
        </p>
        <p className="mt-2">
          3.2 <span className="font-medium text-white">Term.</span> The initial term of
          this Agreement shall be{' '}
          <Field value={contractDuration} label="DURATION" /> commencing on the date of
          full execution (the "Initial Term"), unless extended by exercise of the Label's
          options as described in Section 1.2.
        </p>
      </div>

      {/* Section 4: Rights & Ownership */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-2">
          4. Rights & Ownership
        </h2>
        <p>
          4.1 All master recordings produced under this Agreement shall be considered
          "works made for hire" and shall be the exclusive property of the Label. In the
          event any recording is not deemed a work made for hire, the Artist hereby
          irrevocably assigns all right, title, and interest in such recordings to the
          Label.
        </p>
        <p className="mt-2">
          4.2 The Label shall have the exclusive right to manufacture, distribute,
          market, and otherwise exploit the master recordings in any and all formats and
          media, whether now known or hereafter devised, throughout the Territory.
        </p>
        <p className="mt-2">
          4.3 The Artist retains underlying songwriting and publishing rights, subject to
          any separate publishing agreement between the Parties.
        </p>
      </div>

      {/* Section 5: Termination */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-2">5. Termination</h2>
        <p>
          5.1 Either Party may terminate this Agreement upon written notice if the other
          Party materially breaches any provision hereof and fails to cure such breach
          within thirty (30) days of written notice.
        </p>
        <p className="mt-2">
          5.2 Upon termination, all rights to the master recordings produced during the
          Term shall remain with the Label. Any unrecouped advance balance shall remain
          the obligation of the Artist to the extent specified herein.
        </p>
      </div>

      {/* Signature Block */}
      <div className="pt-6 border-t border-white/10">
        <p className="text-neutral-400 text-xs mb-6 uppercase tracking-wider">
          IN WITNESS WHEREOF, the Parties have executed this Agreement as of the date
          set forth below.
        </p>
        <div className="grid grid-cols-2 gap-12">
          <div className="space-y-4">
            <p className="text-white font-medium">
              {labelCompany || 'LABEL'}
            </p>
            <div className="border-b border-white/20 pb-1">
              <p className="text-neutral-500 text-xs">Signature</p>
            </div>
            <div className="border-b border-white/20 pb-1">
              <p className="text-neutral-500 text-xs">Print Name & Title</p>
            </div>
            <div className="border-b border-white/20 pb-1">
              <p className="text-neutral-500 text-xs">Date</p>
            </div>
          </div>
          <div className="space-y-4">
            <p className="text-white font-medium">
              {artistName || 'ARTIST'}
            </p>
            <div className="border-b border-white/20 pb-1">
              <p className="text-neutral-500 text-xs">Signature</p>
            </div>
            <div className="border-b border-white/20 pb-1">
              <p className="text-neutral-500 text-xs">Print Name</p>
            </div>
            <div className="border-b border-white/20 pb-1">
              <p className="text-neutral-500 text-xs">Date</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function numberWord(n: string): string {
  const words: Record<string, string> = {
    '1': 'one',
    '2': 'two',
    '3': 'three',
    '4': 'four',
    '5': 'five',
    '6': 'six',
    '7': 'seven',
  };
  return words[n] ?? n;
}
